import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import { MealGroup as MealGroupModel } from '../../../models/tracks/mealGroup';
import useDebounce from '../../../hooks/debounce';
import { tracker } from '../../../services/tracker';
import { Food, Foods } from '../../../models/tracks/food';
import SearchResults from '../SearchResults';
import ServingPage from '../ServingPage';
import Button from '../../components/Button';
import { useParams } from 'react-router-dom';
import { analyticsApi } from '../../../services/analytics';

type Props = {
  mealGroup: MealGroupModel;
  trackId?: string;
  includeSearch?: boolean;
  onRemoveMeal?: () => void;
};

type SelectedFood = {
  measureValue: number;
  measureOption: string;
  item: Food;
};

const MealGroup = (props: Props) => {
  const [foods, setFoods] = useState<Food[]>([]);
  const [selectedFoods, setSelectedFoods] = useState<SelectedFood[]>([]);

  const [showServingPage, setShowServingPage] = useState<boolean>(false);
  const [selectedForServing, setSelectedForServing] = useState<Food>();

  const [search, setSearch] = useState<string>('');
  const searchDebounced: string = useDebounce<string>(search, 500);

  const params = useParams<{ trackId: string; groupId: string }>();

  useEffect(() => {
    if (searchDebounced) {
      tracker.searchFood(searchDebounced).then((data) => {
        setFoods(new Foods(data).foods);
      });
    }
  }, [searchDebounced]);

  const appendToSelected = (item: Food, measureValue, measureOption) => {
    setSelectedFoods([
      ...selectedFoods,
      {
        measureOption: measureOption,
        measureValue: measureValue,
        item: item,
      },
    ]);
    setShowServingPage(false);
  };

  const openServingPage = (item: Food) => {
    const isExists = selectedFoods.find(
      (food) => food.item.food.foodId === item.food.foodId,
    );
    if (isExists) {
      setSelectedFoods(
        selectedFoods.filter(
          (food) => food.item.food.foodId !== item.food.foodId,
        ),
      );
    } else {
      setSelectedForServing(item);
      setShowServingPage(true);
    }
  };

  const onSave = async () => {
    await tracker.saveMeals({
      trackId: params.trackId,
      mealGroupId: params.groupId,
      meals: props.mealGroup.meals,
    });
    window.location.href = '/tracker'
  };

  const removeMeal = async (mealId: string, externalId: string) => {
    if(!mealId) {
      props.mealGroup.removeMealByExternalId(externalId)
    } else {
      props.mealGroup.removeMeal(mealId)
      await tracker.removeMeal(props.trackId, mealId);
    }
    props.onRemoveMeal()
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.head}>
        <div className={styles.column}>{props.mealGroup.value}</div>
        <div className={styles.column}>fat</div>
        <div className={styles.column}>carb</div>
        <div className={styles.column}>prot</div>
        <div className={styles.column}>kcal</div>
        <div className={styles.column} />
      </div>
      {props.includeSearch && !showServingPage && (
        <div className={styles.search_group}>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            placeholder={'Enter product or recipe'}
            className={`${styles.search_input}`}
          />
          {search && (
            <div className={styles.close} onClick={() => setSearch('')} />
          )}
        </div>
      )}
      {props.mealGroup.meals.length > 0 && !foods.length && !showServingPage && (
        <>
          <div className={styles.body}>
            {props.mealGroup.meals.map((meal) => {
              return (
                <div key={meal._id} className={styles.item}>
                  <div className={styles.column}>
                    <div className={styles.meal_name}>{meal.name}</div>
                    <div className={styles.meal_measure}>
                      {meal.count} {meal.measurement}
                    </div>
                  </div>
                  <div className={styles.column}>{meal.calculate('fat')}</div>
                  <div className={styles.column}>{meal.calculate('carb')}</div>
                  <div className={styles.column}>{meal.calculate('prot')}</div>
                  <div className={styles.column}>{meal.calculate('kcal')}</div>
                  <div className={styles.column}>
                    <img src="/assets/images/minus.svg" alt="" onClick={() => removeMeal(meal._id, meal.externalId)} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className={styles.footer}>
            <div className={styles.column}>Total:</div>
            <div className={styles.column}>
              {props.mealGroup.getTotal('fat') || '-'}
            </div>
            <div className={styles.column}>
              {props.mealGroup.getTotal('carb') || '-'}
            </div>
            <div className={styles.column}>
              {props.mealGroup.getTotal('prot') || '-'}
            </div>
            <div className={styles.column}>
              {props.mealGroup.getTotal('kcal') || '-'}
            </div>
            <div className={styles.column}>
              {!props.includeSearch && props.trackId && (
                <a
                  href={`/tracker/${props.trackId}/group/${props.mealGroup._id}/meal`}
                  onClick={() => analyticsApi.logEvent(
                    '',
                    'Action',
                    { meal: props.mealGroup.value },
                    'Add Product Clicked'
                  )}
                >
                  <img src="/assets/images/plus.svg" alt="" />
                </a>
              )}
            </div>
          </div>
          {props.includeSearch && (
            <div className={styles.button_group}>
              <Button type={'success'} onClick={onSave}>
                Done
              </Button>
            </div>
          )}
        </>
      )}
      {foods.length > 0 && !showServingPage && (
        <SearchResults
          onAdd={() => {
            props.mealGroup.appendMeal(selectedFoods);
            setFoods([]);
            setSelectedFoods([]);
            setSearch('');
          }}
          selected={selectedFoods.map((item) => item.item)}
          onClick={(item) => {
            openServingPage(item);
          }}
          foods={foods}
        />
      )}
      {showServingPage && selectedForServing && (
        <ServingPage
          onAdd={(item, measureValue, measureOption) =>
            appendToSelected(item, measureValue, measureOption)
          }
          item={selectedForServing}
        />
      )}
      {
        props.mealGroup.meals.length === 0 && !foods.length && !showServingPage && (
          <div className={styles.empty_result}>
            <img src="/assets/images/empty_results.png" alt="" />
            <p>“Tap into field to search <br/>
              products & meals”</p>
          </div>
        )
      }
    </div>
  );
};

export default MealGroup;
